import {
    RiMessage2Line,
    RiStopLine, RiVideoChatLine,
} from "@remixicon/react";
import React, {
    FormEvent, ReactNode, useEffect, useState,
} from "react";
import { RiCheckLine } from "react-icons/ri";
import { CreateUploadTokenDocument } from "../../__generated__/api";
import client from "../../api";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import useCurrentSession from "../../contexts/session";
import Button from "../theme/button/Button";
import Dropdown from "../theme/dropdown/Dropdown";
import FormGroup from "../theme/form-group/FormGroup";
import Toggle from "../theme/toggle/Toggle";
import Styles from "./Feedback.module.scss";
import { HelpTypes, useOnHelpEvent } from "../../contexts/HelpContext";

const SUBMIT_URL = process.env.REACT_APP_FEEDBACK_URL ?? "";

interface BodyUser {
    name: string | undefined;
    email: string | undefined;
    sessionId: string;
    userId: string;
}
interface Body {
    text: string;
    isAnonymous: boolean;
    user?: BodyUser;
    videoUrl?: string;
}

async function startVideo(onStop: ((url: string) => void)) {
    try {
        const meta = await client.mutate({
            mutation: CreateUploadTokenDocument,
            variables: {
                attachment: {
                    filename: "test.webm",
                    size: 0,
                    mimeType: "video/webm",
                },
            },
        });

        const { url } = meta.data.createUploadToken;

        type DisplayType = Parameters<typeof navigator.mediaDevices.getDisplayMedia>[0];
        const stream = await navigator.mediaDevices.getDisplayMedia({
            preferCurrentTab: true,
            video: true,
            audio: true,
        } as DisplayType);
        const recorder = new MediaRecorder(stream, {
            mimeType: "video/webm",
        });

        const chunks: ConstructorParameters<typeof Blob>[0] = [];
        recorder.addEventListener("dataavailable", async event => {
            chunks.push(event.data);
        });

        const promise = new Promise<string>((resolve, reject) => {
            recorder.onstop = async () => {
                try {
                    const blob = new Blob(chunks, { type: "video/webm" });
                    const file = new File([blob], "test.webm", { type: "video/webm" });
                    const result = await fetch(url, {
                        method: "POST",
                        credentials: "include",
                        body: file,
                    });
                    LoggingHandler.instance.info({ status: result.status }, "Upload feedback complete");

                    const data = await result.json() as {url: string};
                    resolve(data.url);
                    if (onStop) {
                        LoggingHandler.instance.nextTick(async () => onStop(data.url));
                    }
                } catch (err) {
                    reject(err);
                }
            };
        });

        recorder.start();

        return {
            stop: async () => {
                try {
                    recorder.stop();
                    stream.getTracks().forEach(track => track.stop());
                    return await promise;
                } catch (err) {
                    LoggingHandler.instance.error({ err }, "Error starting recording");
                    return undefined;
                }
            },
        };
    } catch (err) {
        LoggingHandler.instance.error({ err }, "Error starting recording");
        return undefined;
    }
}

export default function Feedback({ metricPathPrefix }: {metricPathPrefix?: string[]}) {
    const session = useCurrentSession();
    const [isShow, setShow] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [recorder, setRecorder] = useState<{stop:() => Promise<string | undefined>}>();

    const [text, setText] = useState("");
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string>();

    const stopRecording = async () => {
        setIsProcessing(true);
        setRecorder(undefined);
        if (recorder) {
            setVideoUrl(await recorder?.stop());
        }
        setIsProcessing(false);
    };
    const startRecording = async () => {
        setRecorder(await startVideo(url => {
            setIsProcessing(true);
            setRecorder(undefined);
            if (recorder) {
                setVideoUrl(url);
            }
            setIsProcessing(false);
        }));
    };
    const submit = async (evt: FormEvent) => {
        evt.preventDefault();
        setIsDone(true);
        LoggingHandler.instance.wrap(async () => {
            const body: Body = {
                text,
                isAnonymous,
                videoUrl,
            };
            if (!isAnonymous) {
                body.user = {
                    name: session.user?.name as string,
                    email: session.user?.email,
                    sessionId: session.current?.id,
                    userId: session.userId as string,
                };
            }

            await fetch(SUBMIT_URL, {
                headers: {
                    "content-type": "application/json",
                },
                method: "POST",
                body: JSON.stringify(body),
            });
            setIsProcessing(false);
            setText("");
            setVideoUrl(undefined);
            setRecorder(undefined);
            setIsAnonymous(false);

            setTimeout(() => setShow(false), 4000);
        });
    };

    useOnHelpEvent(HelpTypes.FEEDBACK, () => setShow(true));

    useEffect(() => {
        setIsDone(false);
    }, [isShow]);

    let content: ReactNode;

    if (recorder) {
        content = (
            <div className={Styles.thanks}>
                Screen is recording. When you are done, click Stop Recording below

                <Button prefixIcon={<RiStopLine />} onClick={stopRecording}>
                    Stop Recording
                </Button>
            </div>
        );
    } else if (isDone) {
        content = (
            <div className={Styles.thanks}>
                <div className={Styles["feedback-icon"]}>
                    <RiCheckLine />
                </div>
                Thank you for your feedback! It is super important to us!
            </div>
        );
    } else {
        content = (
            <form onSubmit={submit}>
                <FormGroup
                    label="Feedback"
                    type="text"
                    display="textarea"
                    isRequired
                    placeholder="I love..."
                    value={text}
                    onChange={value => setText(value.target.value)}
                />
                <div className={Styles.contact}>
                    <Toggle
                        text="Submit anonymously"
                        checked={isAnonymous}
                        onChange={value => setIsAnonymous(value.target.checked)}
                    />
                </div>
                <div className={Styles.contact}>
                    {(videoUrl || isProcessing) ? (
                    /* eslint-disable jsx-a11y/media-has-caption */
                        <div>
                            {isProcessing ? (
                                <div className={Styles.thanks}>
                                    Your video is uploading...
                                </div>
                            ) : (
                                <>
                                    <video className={Styles.video} controls>
                                        <source src={videoUrl} type="video/webm" />
                                    </video>
                                    <Button
                                        variant="none"
                                        size="small"
                                        onClick={() => setVideoUrl(undefined)}
                                        isClose
                                    >
                                        Remove Recording
                                    </Button>
                                </>
                            )}

                        </div>
                    /* eslint-enable jsx-a11y/media-has-caption */
                    ) : (
                        <Button
                            variant="outline-gray"
                            size="small"
                            onClick={startRecording}
                            prefixIcon={<RiVideoChatLine />}
                        >
                            Add Screen Recording
                        </Button>
                    )}

                </div>
                <div className={Styles.buttons}>
                    <Button variant="outline-gray" size="small" onClick={() => setShow(false)}>Cancel</Button>
                    <Button variant="primary" type="submit" size="small">Submit</Button>
                </div>
            </form>
        );
    }

    return (
        <Dropdown onToggle={value => setShow(value)} show={isShow}>
            <Dropdown.Toggle
                metricPath={metricPathPrefix ? [...metricPathPrefix, "feedback", "show"] : undefined}
                prefixIcon={<RiMessage2Line />}
                size="small"
                variant="ghost-gray"
            />
            <Dropdown.Menu className={Styles.feedback}>
                {content}
            </Dropdown.Menu>
        </Dropdown>
    );
}

Feedback.defaultProps = {
    metricPathPrefix: undefined,
};
